import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const DiagnosisTab = forwardRef(({ inpatientId }, ref) => {
    const { t } = useTranslation();
    const [initialDiagnosis, setInitialDiagnosis] = useState('');
    const [differentialDiagnoses, setDifferentialDiagnoses] = useState([]);
    const [finalDiagnosis, setFinalDiagnosis] = useState('');

    // Refs for safe async operations
    const isMounted = React.useRef(true);
    const saveTimerRef = React.useRef(null);
    const diagnosisRef = React.useRef('');

    useEffect(() => {
        diagnosisRef.current = initialDiagnosis;
    }, [initialDiagnosis]);

    const calculateFinalDiagnosis = useCallback(() => {
        const current = initialDiagnosis.trim();
        if (current) {
            setFinalDiagnosis(current);
        } else if (differentialDiagnoses && differentialDiagnoses.length > 0) {
            // Last differential is the final diagnosis
            const lastDifferential = differentialDiagnoses[differentialDiagnoses.length - 1];
            setFinalDiagnosis(lastDifferential.diagnosis || '');
        } else {
            setFinalDiagnosis('');
        }
    }, [initialDiagnosis, differentialDiagnoses]);

    const saveDiagnosisData = useCallback(async () => {
        if (!inpatientId) return;

        try {
            const token = localStorage.getItem('access_token');

            // First, fetch the latest SOAP data to preserve all existing fields
            let existingSoapData = {};
            try {
                const fetchResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${inpatientId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });
                if (fetchResponse.ok) {
                    const fetchJson = await fetchResponse.json();
                    if (fetchJson.data && fetchJson.data.length > 0) {
                        existingSoapData = fetchJson.data[0];
                    }
                }
            } catch (fetchErr) {
                console.error("Failed to fetch existing SOAP data before save:", fetchErr);
            }

            // Construct payload preserving ALL existing SOAP data
            const payload = {
                inpatient_visit_id: parseInt(inpatientId),
                serial: existingSoapData?.serial || null,
                date_time: existingSoapData?.date_time || new Date().toISOString(),
                owner_complaint: existingSoapData?.owner_complaint || '', // Preserve chief complaint
                objective: existingSoapData?.objective || '',
                severity: existingSoapData?.severity || '',
                initial_diagnosis: diagnosisRef.current || initialDiagnosis,
                assessment: existingSoapData?.assessment || '',
                plan: existingSoapData?.plan || '',
                diagnosis_history: differentialDiagnoses,
                final_diagnosis: finalDiagnosis
            };

            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                console.error('Failed to save diagnosis data');
            }
        } catch (error) {
            console.error('Error saving diagnosis data:', error);
        }
    }, [inpatientId, initialDiagnosis, differentialDiagnoses, finalDiagnosis]);

    useImperativeHandle(ref, () => ({
        save: async () => {
            await saveDiagnosisData();
        }
    }));

    useEffect(() => {
        return () => {
            isMounted.current = false;
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, []);

    const fetchDiagnosisData = useCallback(async () => {
        if (!inpatientId) return;
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/soap-notes/inpatient-visit/${inpatientId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (isMounted.current && response.ok) {
                const result = await response.json();

                // API returns array, extract first element
                const soapData = Array.isArray(result.data) ? result.data[0] : result.data;

                if (soapData) {
                    // Use snake_case field names from API
                    setInitialDiagnosis(soapData.initial_diagnosis || '');

                    // Parse diagnosis history - check both 'history' and 'diagnosis_history'
                    const historyField = soapData.diagnosis_history;
                    if (historyField) {
                        try {
                            const history = typeof historyField === 'string'
                                ? JSON.parse(historyField)
                                : historyField;
                            setDifferentialDiagnoses(Array.isArray(history) ? history : []);
                        } catch (e) {
                            console.error('Error parsing diagnosis history:', e);
                            setDifferentialDiagnoses([]);
                        }
                    }

                    setFinalDiagnosis(soapData.final_diagnosis || '');
                }
            }
        } catch (error) {
            console.error('Error fetching diagnosis data:', error);
        }
    }, [inpatientId]);

    // Fetch diagnosis data on mount
    useEffect(() => {
        fetchDiagnosisData();
    }, [fetchDiagnosisData]);

    // Calculate final diagnosis whenever data changes
    useEffect(() => {
        calculateFinalDiagnosis();
    }, [calculateFinalDiagnosis]);

    const debouncedSave = useCallback(() => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            saveDiagnosisData();
        }, 1000);
    }, [saveDiagnosisData]);

    const handleInitialDiagnosisChange = (value) => {
        setInitialDiagnosis(value);
        debouncedSave();
    };

    const handleAddDifferential = () => {
        if (!initialDiagnosis.trim()) return;

        // Get user name from localStorage user_data JSON object
        let userName = t('common.doctor', { defaultValue: 'Doctor' });
        try {
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                // Construct full name from first_name and last_name
                if (userData.first_name && userData.last_name) {
                    const fullName = `${userData.first_name} ${userData.last_name}`;
                    // Add Dr. prefix for veterinarians
                    const doctorPrefix = t('common.doctorPrefix', { defaultValue: 'Dr.' });
                    userName = userData.user_type === 'VETERINARIAN' ? `${doctorPrefix} ${fullName}` : fullName;
                } else {
                    userName = userData.name || userData.username || t('common.doctor', { defaultValue: 'Doctor' });
                }
            }
        } catch (e) {
            console.error('DiagnosisTab: Error parsing user_data:', e);
        }

        // Push current diagnosis into history
        const newDifferential = {
            diagnosis: initialDiagnosis,
            date: new Date().toISOString(),
            by: userName
        };

        setDifferentialDiagnoses(prev => [...prev, newDifferential]);
        setInitialDiagnosis(''); // Clear for next differential
        debouncedSave();
    };

    const handleDeleteDifferential = (indexToRemove) => {
        setDifferentialDiagnoses(prev => {
            const updated = prev.filter((_, idx) => idx !== indexToRemove);
            return updated;
        });
        debouncedSave();
    };

    const handleBlur = () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveDiagnosisData();
    };

    return (
        <div className="diagnosis-content tab-content-inner">
            {/* Initial Diagnosis */}
            <div className="diagnosis-section" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#374151',
                        marginBottom: 0
                    }}>
                        {t('receptionInpatients.details.diagnosis.currentDiagnosis', { defaultValue: 'Current Diagnosis' })}
                        <span style={{ color: '#EF4444', marginLeft: '4px' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            type="button"
                            onClick={() => {
                                setInitialDiagnosis('');
                                debouncedSave();
                            }}
                            disabled={!initialDiagnosis.trim()}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 14px',
                                backgroundColor: initialDiagnosis.trim() ? '#FEE2E2' : '#F9FAFB',
                                color: initialDiagnosis.trim() ? '#B91C1C' : '#9CA3AF',
                                border: `1px solid ${initialDiagnosis.trim() ? '#FCA5A5' : '#E5E7EB'}`,
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: 500,
                                cursor: initialDiagnosis.trim() ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s'
                            }}
                        >
                            <i className="fas fa-eraser" style={{ fontSize: '12px' }}></i>
                            {t('common.clear', { defaultValue: 'Clear' })}
                        </button>
                        <button
                            type="button"
                            onClick={handleAddDifferential}
                            disabled={!initialDiagnosis.trim()}
                            title={t('receptionInpatients.details.diagnosis.addToHistoryTooltip', { defaultValue: 'Add the current diagnosis as a differential entry and clear the field for the next one.' })}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 14px',
                                backgroundColor: initialDiagnosis.trim() ? '#2563EB' : '#E5E7EB',
                                color: initialDiagnosis.trim() ? '#FFFFFF' : '#9CA3AF',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: 500,
                                cursor: initialDiagnosis.trim() ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s'
                            }}
                        >
                            <i className="fas fa-plus" style={{ fontSize: '12px' }}></i>
                            {t('receptionInpatients.details.diagnosis.addDifferential', { defaultValue: 'Add Differential Diagnosis' })}
                        </button>
                    </div>
                </div>
                <div style={{
                    fontSize: '12px',
                    color: '#6B7280',
                    marginBottom: '8px',
                    fontStyle: 'italic'
                }}>
                    💡 {t('receptionInpatients.details.diagnosis.helpText', { defaultValue: 'Use “Add Differential Diagnosis” to move the current diagnosis into the differential list below and clear the field for the next entry.' })}
                </div>
                <textarea
                    value={initialDiagnosis}
                    onChange={(e) => handleInitialDiagnosisChange(e.target.value)}
                    onBlur={handleBlur}
                    placeholder={t('receptionInpatients.details.diagnosis.initialPlaceholder', { defaultValue: 'Enter diagnosis...' })}
                    rows={4}
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #D1D5DB',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                    }}
                />
            </div>

            {/* Differential Diagnosis History */}
            {
                differentialDiagnoses.length > 0 && (
                    <div className="diagnosis-section" style={{ marginBottom: '24px' }}>
                        <label style={{
                            display: 'block',
                            fontWeight: 600,
                            marginBottom: '12px',
                            fontSize: '14px',
                            color: '#374151'
                        }}>
                            {t('receptionInpatients.details.diagnosis.diagnosticHistory', { defaultValue: 'Diagnostic History' })}
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {[...differentialDiagnoses].reverse().map((entry, index) => {
                                const originalIndex = differentialDiagnoses.length - 1 - index;
                                return (
                                    <div key={index} style={{ position: 'relative' }}>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#6B7280',
                                            marginBottom: '6px',
                                            display: 'flex',
                                            justifyContent: 'space-between'
                                        }}>
                                            <span style={{ fontWeight: 500 }}>
                                                {originalIndex === 0
                                                    ? t('receptionInpatients.details.diagnosis.initialDiagnosis', { defaultValue: 'Initial Diagnosis' })
                                                    : t('receptionInpatients.details.diagnosis.differentialNumber', {
                                                        number: originalIndex,
                                                        defaultValue: `Differential ${originalIndex}`
                                                    })
                                                }
                                            </span>
                                            <span>{entry.by} • {new Date(entry.date).toLocaleString()}</span>
                                        </div>
                                        <div style={{ position: 'relative' }}>
                                            <textarea
                                                value={entry.diagnosis}
                                                disabled
                                                rows={4}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    borderRadius: '8px',
                                                    border: '1px solid #E5E7EB',
                                                    backgroundColor: '#F9FAFB',
                                                    cursor: 'not-allowed',
                                                    opacity: 0.8,
                                                    color: '#374151',
                                                    fontSize: '14px',
                                                    fontFamily: 'inherit'
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteDifferential(originalIndex)}
                                                style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    background: '#FEE2E2',
                                                    color: '#B91C1C',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer'
                                                }}
                                                title={t('receptionInpatients.details.diagnosis.deleteDifferential', { defaultValue: 'Delete this differential diagnosis' })}
                                            >
                                                {t('common.delete', { defaultValue: 'Delete' })}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )
            }

            {/* Final Diagnosis (Read-only, calculated) */}
            <div className="diagnosis-section">
                <label style={{
                    display: 'block',
                    fontWeight: 600,
                    marginBottom: '8px',
                    fontSize: '14px',
                    color: '#374151'
                }}>
                    {t('receptionInpatients.details.diagnosis.finalDiagnosis', { defaultValue: 'Final Diagnosis' })}
                </label>
                <textarea
                    value={finalDiagnosis}
                    disabled
                    placeholder={t('receptionInpatients.details.diagnosis.finalPlaceholder', { defaultValue: 'Automatically calculated from the latest diagnosis input or history' })}
                    rows={4}
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        backgroundColor: finalDiagnosis ? '#FFFBEB' : '#F9FAFB',
                        cursor: 'not-allowed',
                        opacity: finalDiagnosis ? 1 : 0.7,
                        color: '#374151',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        fontWeight: finalDiagnosis ? 600 : 400
                    }}
                />
            </div>
        </div >
    );
});

export default DiagnosisTab;
